// =====================================================
// INSURANCE COMPANIES ADMIN PAGE - Aseguradoras
// CRUD completo con conteo de pólizas + Import/Export
// Mejorado: feedback claro al desactivar
// Validación de código duplicado con error inline
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Building2,
  Plus,
  Search,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  Phone,
  Mail,
  FileText,
  Filter,
  FileSpreadsheet,
  Eye,
  EyeOff,
} from 'lucide-react';
import { insuranceCompaniesApi } from '../../api/insurance-companies';
import { insuranceCompaniesImportExport } from '../../api/import-export';
import type { InsuranceCompany } from '../../api/insurance-companies';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ImportExportModal } from '../../components/ui/ImportExportModal';
import { toast } from '../../components/ui/Toast';

interface InsuranceCompanyFormData {
  code: string;
  name: string;
  phone: string;
  email: string;
}

export function InsuranceCompaniesPage() {
  // Data state
  const [items, setItems] = useState<InsuranceCompany[]>([]);
  const [filteredItems, setFilteredItems] = useState<InsuranceCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Sorting and pagination
  type SortField = 'name' | 'code' | 'createdAt';
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InsuranceCompany | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Import/Export modal
  const [isImportExportOpen, setIsImportExportOpen] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: InsuranceCompany | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InsuranceCompanyFormData>({
    defaultValues: {
      code: '',
      name: '',
      phone: '',
      email: '',
    },
  });

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await insuranceCompaniesApi.getAll(showInactive);
      setItems(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar aseguradoras');
    } finally {
      setIsLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter, sort and paginate items
  useEffect(() => {
    let result = items;

    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.code.toLowerCase().includes(query) ||
          item.name.toLowerCase().includes(query) ||
          item.email?.toLowerCase().includes(query) ||
          item.phone?.toLowerCase().includes(query)
      );
    }

    // Ordenar
    result = [...result].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortBy) {
        case 'code':
          aVal = a.code.toLowerCase();
          bVal = b.code.toLowerCase();
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt || 0).getTime();
          bVal = new Date(b.createdAt || 0).getTime();
          break;
        default:
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredItems(result);
  }, [items, searchQuery, sortBy, sortOrder]);

  // Paginación calculada
  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / limit);
  const paginatedItems = filteredItems.slice((page - 1) * limit, page * limit);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, showInactive, sortBy, sortOrder, limit]);

  // Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    reset({
      code: '',
      name: '',
      phone: '',
      email: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (item: InsuranceCompany) => {
    setEditingItem(item);
    setFormError(null);
    reset({
      code: item.code,
      name: item.name,
      phone: item.phone || '',
      email: item.email || '',
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: InsuranceCompanyFormData) => {
    // Limpiar error previo
    setFormError(null);

    // Validar código duplicado
    const codeUpper = formData.code.toUpperCase().trim();
    const existingItem = items.find(
      (item) => item.code.toUpperCase() === codeUpper && item.id !== editingItem?.id
    );

    if (existingItem) {
      setFormError(`Ya existe una aseguradora con el código "${codeUpper}"`);
      return;
    }

    setIsSaving(true);
    try {
      if (editingItem) {
        await insuranceCompaniesApi.update(editingItem.id, {
          code: formData.code,
          name: formData.name,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
        });
        toast.success('Actualizada', `Aseguradora "${formData.name}" actualizada`);
      } else {
        await insuranceCompaniesApi.create({
          code: formData.code,
          name: formData.name,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
        });
        toast.success('Creada', `Aseguradora "${formData.name}" creada`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'No se pudo guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: InsuranceCompany) => {
    if (item.code === 'PENDING') {
      toast.error('No permitido', 'La aseguradora PENDING no se puede desactivar');
      return;
    }
    setConfirmDialog({
      isOpen: true,
      item,
      action: item.isActive ? 'deactivate' : 'activate',
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await insuranceCompaniesApi.deactivate(confirmDialog.item.id);
        toast.success(
          'Desactivada',
          `"${confirmDialog.item.name}" desactivada. Activa "Mostrar inactivas" para verla.`
        );
        // Activar automáticamente el filtro si no está activo para que vea el elemento
        if (!showInactive) {
          setShowInactive(true);
          setShowFilters(true);
        }
      } else {
        await insuranceCompaniesApi.reactivate(confirmDialog.item.id);
        toast.success('Activada', `Aseguradora "${confirmDialog.item.name}" activada`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  // Contar inactivas para badge
  const inactiveCount = items.filter((i) => !i.isActive).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aseguradoras</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestiona las aseguradoras y sus datos de contacto
          </p>
        </div>
      </div>

      {/* Toolbar - Nuevo diseño */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        {/* Fila superior: Búsqueda y acciones principales */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Búsqueda y botón filtros */}
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por código, nombre, teléfono o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters 
                  ? 'bg-blue-50 border-blue-200 text-blue-600' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
              {inactiveCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                  {inactiveCount}
                </span>
              )}
            </button>
          </div>

          {/* Acciones */}
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsImportExportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Importar / Exportar
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nueva aseguradora
            </button>
          </div>
        </div>

        {/* Fila de filtros activos (chips) */}
        {(showInactive || searchQuery) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Estado: Incluye inactivos
                <button 
                  onClick={() => setShowInactive(false)}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button 
                  onClick={() => setSearchQuery('')}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            <button 
              onClick={() => {
                setShowInactive(false);
                setSearchQuery('');
              }}
              className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2"
            >
              Limpiar todo
            </button>
          </div>
        )}

        {/* Panel de filtros */}
        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-100 pt-3 gap-4">
            {/* Checkbox y ordenación */}
            <div className="flex items-center gap-6 flex-wrap">
              <label className="inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors flex items-center gap-1.5">
                  {showInactive ? (
                    <Eye className="h-4 w-4 text-blue-600" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  )}
                  Mostrar inactivas
                  {inactiveCount > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                      {inactiveCount}
                    </span>
                  )}
                </span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="code-desc">Código (Z-A)</option>
                  <option value="createdAt-desc">Fecha (Más reciente)</option>
                  <option value="createdAt-asc">Fecha (Antiguo)</option>
                </select>
              </div>
            </div>

            {/* Items por página */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Ver:</span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLimit(10)}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 10 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  10
                </button>
                <button
                  onClick={() => setLimit(25)}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 25 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  25
                </button>
                <button
                  onClick={() => setLimit(50)}
                  className={`px-3 py-1 text-xs font-semibold transition-colors ${
                    limit === 50 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  50
                </button>
              </div>
              <span className="text-sm text-gray-500">por página</span>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-red-500">
            <AlertCircle className="h-8 w-8 mb-2" />
            <p>{error}</p>
            <button
              onClick={loadData}
              className="mt-4 text-sm text-blue-600 hover:underline"
            >
              Reintentar
            </button>
          </div>
        ) : paginatedItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Building2 className="h-12 w-12 mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-900">
              No hay aseguradoras
            </p>
            <p className="text-sm">
              {searchQuery
                ? 'No se encontraron resultados para tu búsqueda'
                : 'Crea la primera aseguradora'}
            </p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Contacto
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Pólizas
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedItems.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      !item.isActive ? 'bg-gray-50/50 opacity-60' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-mono font-medium">
                        {item.code}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-sm">
                          {item.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{item.name}</p>
                          {!item.isActive && (
                            <span className="text-xs text-amber-600 flex items-center gap-1">
                              <Power className="h-3 w-3" /> Inactiva
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm space-y-1">
                        {item.phone && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Phone className="h-3.5 w-3.5" />
                            {item.phone}
                          </div>
                        )}
                        {item.email && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Mail className="h-3.5 w-3.5" />
                            {item.email}
                          </div>
                        )}
                        {!item.phone && !item.email && (
                          <span className="text-gray-400 text-xs">Sin contacto</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {item.policiesCount ?? 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleStatus(item)}
                        disabled={item.code === 'PENDING'}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          item.isActive ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        role="switch"
                        aria-checked={item.isActive}
                        title={item.isActive ? 'Desactivar' : 'Activar'}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            item.isActive ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        >
                          <Power
                            className={`h-3 w-3 absolute top-1 left-1 ${
                              item.isActive ? 'text-blue-600' : 'text-gray-400'
                            }`}
                          />
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Mostrando {((page - 1) * limit) + 1} - {Math.min(page * limit, totalItems)} de {totalItems}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-gray-600">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingItem ? 'Editar aseguradora' : 'Nueva aseguradora'}
        subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Completa los datos'}
        size="md"
      >
        <form onSubmit={handleSubmit(handleSave)}>
          {/* Error banner dentro del modal */}
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800">Error de validación</p>
                <p className="text-sm text-red-600 mt-0.5">{formError}</p>
              </div>
              <button
                type="button"
                onClick={() => setFormError(null)}
                className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código *
                </label>
                <input
                  type="text"
                  {...register('code', { 
                    required: 'El código es obligatorio',
                    maxLength: { value: 20, message: 'Máximo 20 caracteres' }
                  })}
                  onChange={() => formError && setFormError(null)}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${
                    errors.code || formError ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
                  }`}
                  placeholder="ASE001"
                />
                {errors.code && (
                  <p className="mt-1 text-xs text-red-500">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    {errors.code.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  {...register('name', { 
                    required: 'El nombre es obligatorio',
                    maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                  })}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm ${
                    errors.name ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Nombre de la aseguradora"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-500">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    {errors.name.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="tel"
                    {...register('phone', {
                      maxLength: { value: 20, message: 'Máximo 20 caracteres' }
                    })}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                    placeholder="+34 912 345 678"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    {...register('email', {
                      maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                    })}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                    placeholder="contacto@aseguradora.com"
                  />
                </div>
              </div>
            </div>
          </div>

          <ModalFooter>
            <button
              type="button"
              onClick={handleCloseModal}
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {editingItem ? 'Guardar' : 'Crear'}
                </>
              )}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Import/Export Modal */}
      <ImportExportModal
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
        title="Aseguradoras"
        onExport={insuranceCompaniesImportExport.export}
        onDownloadTemplate={insuranceCompaniesImportExport.downloadTemplate}
        onPreview={insuranceCompaniesImportExport.preview}
        onImport={insuranceCompaniesImportExport.import}
        onImportSuccess={loadData}
        previewColumns={[
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'phone', label: 'Teléfono' },
          { key: 'email', label: 'Email' },
        ]}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() =>
          !isProcessing &&
          setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })
        }
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={
          confirmDialog.action === 'deactivate'
            ? '¿Desactivar aseguradora?'
            : '¿Activar aseguradora?'
        }
        message={
          confirmDialog.action === 'deactivate'
            ? `La aseguradora "${confirmDialog.item?.name}" será desactivada. Podrás verla activando "Mostrar inactivas".`
            : `La aseguradora "${confirmDialog.item?.name}" será reactivada.`
        }
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default InsuranceCompaniesPage;
